import * as React from 'react';
import {Component, ReactElement} from 'react';
import {getSettingsChanges, ISettingsChanges} from './service';
import {ISelectOption} from '../select/select';
import {VideoSettingsLayout as Layout} from './layout/layout';
import {VideoSettingsHeader as Header} from './header/header';
import {VideoSettingsText as Text} from './text/text';
import {VideoSettingsSection as Section} from './section/section';
import {VideoSettingsColumn as Column} from './column/column';
import {VideoSettingsSoundOutput as SoundOutput} from './sound-output';
import {VideoSettingsMediaInput as MediaInput} from './media-input/media-input';
import {VideoSettingsVideoQuality as VideoQuality} from './video-quality/video-quality';
import {VideoSettingsCameraPreview as CameraPreview} from './camera-preview/camera-preview';
import {VideoSettingsAudioMeter as AudioMeter} from './audio-meter/audio-meter';

import {
  ILoadMediaInput,
  IVideoSettings,
  IMediaInput,
  IDevice,

  AUDIO_INPUT,
  VIDEO_INPUT,
} from '../../redux/modules/video-settings';

export interface IVideoSettingsProps {
  audioOutputDeviceId: string;
  audioInputDeviceId: string;
  videoInputDeviceId: string;
  audioInputDevices: Array<IDevice>;
  videoInputDevices: Array<IDevice>;
  videoQualityError: string;
  audioInputError: string;
  videoInputError: string;
  videoQualityId: string;
  screenType: string;
  isLoading: boolean;
  stream: MediaStream;
  error: string;

  saveVideoSettings?: (props: IVideoSettings) => void;
  selectMediaInput?: (props: IMediaInput) => void;
  setVideoQuality?: (props: IMediaInput) => void;
  loadMedia?: (props: ILoadMediaInput) => void;
  onClose?: () => void;
  onSave?: (props?: ISettingsChanges) => void;
}

export class VideoSettings extends Component<IVideoSettingsProps, {}> {
  private mediaElement: HTMLVideoElement;

  public render(): ReactElement<any> {
    const {
      audioInputDeviceId,
      videoInputDeviceId,
      videoQualityError,
      audioInputDevices,
      videoInputDevices,
      audioInputError,
      videoInputError,
      videoQualityId,
      isLoading,
      stream,
      error,
      onClose,
    } = this.props;

    return (
      <Layout>
        <Header onClose={onClose} />

        <Section column middle>
          <CameraPreview
            mediaElementRef={node => this.mediaElement = node}
            disabled={isLoading}
          />

          <AudioMeter
            disabled={isLoading}
            stream={stream}
          />

          <Text>If you are having problems, try restarting your browser.</Text>
        </Section>

        <Section>
          <Column>
            <MediaInput
              deviceId={videoInputDeviceId}
              devices={videoInputDevices}
              label={'Camera'}
              error={videoInputError}
              type={VIDEO_INPUT}
              onChange={this.selectVideoInput}
            />
          </Column>

          <Column>
            <MediaInput
              deviceId={audioInputDeviceId}
              devices={audioInputDevices}
              label={'Microphone'}
              error={audioInputError}
              type={AUDIO_INPUT}
              onChange={this.selectAudioInput}
            />
          </Column>
        </Section>

        <Section>
          <Column>
            <VideoQuality
              videoQualityId={videoQualityId}
              hidden={!videoInputDevices.length}
              error={videoQualityError}
              onChange={this.selectVideoQuality}
            />
          </Column>

          <Column>
            <SoundOutput />
          </Column>
        </Section>

        <Section middle>
          <Column size={7}>
            <Text>Updating preferences will refresh your browser window.</Text>
            <Text>You will automatically rejoin the session.</Text>
          </Column>

          <Column size={3}>
            <button
              className={'btn btn_lg btn_violet'}
              disabled={isLoading || !!error}
              onClick={this.onSave}
            >
              Save
            </button>
          </Column>
        </Section>
      </Layout>
    );
  }

  public componentDidMount(): void {
    if (this.props.stream) {
      this.startStream();
    }
  }

  public componentWillUnmount(): void {
    this.stopStream();
  }

  public componentDidUpdate(prevProps: IVideoSettingsProps): void {
    const currStreamId: string = this.props.stream
      ? this.props.stream.id
      : '';

    const prevStreamId: string = prevProps.stream
      ? prevProps.stream.id
      : '';

    if (currStreamId !== prevStreamId) {
      this.startStream();
    }
  }

  private selectVideoQuality = ({value: videoQualityId}: ISelectOption) => {
    const {
      audioInputDeviceId,
      videoInputDeviceId,
      setVideoQuality,
    } = this.props;

    this.stopStream();
    setVideoQuality({audioInputDeviceId, videoInputDeviceId, videoQualityId});
  }

  private selectVideoInput = ({value: videoInputDeviceId}: ISelectOption) => {
    const {
      audioInputDeviceId,
      audioInputError,
      videoQualityId,
      selectMediaInput,
    } = this.props;

    this.stopStream();
    selectMediaInput({
      audioInputDeviceId: audioInputError ? null : audioInputDeviceId,
      videoInputDeviceId,
      videoQualityId,
      type: VIDEO_INPUT,
    });
  }

  private selectAudioInput = ({value: audioInputDeviceId}: ISelectOption) => {
    const {
      videoInputDeviceId,
      videoInputError,
      videoQualityId,
      selectMediaInput,
    } = this.props;

    this.stopStream();
    selectMediaInput({
      audioInputDeviceId,
      videoInputDeviceId: videoInputError ? null : videoInputDeviceId,
      videoQualityId,
      type: AUDIO_INPUT,
    });
  }

  private stopStream = (): void => {
    const {stream} = this.props;

    if (stream) {
      stream
        .getTracks()
        .forEach(track => track.stop());
    }

    if (this.mediaElement) {
      this.mediaElement.srcObject = null;
    }
  }

  private startStream = (): void => {
    if (this.props.stream) {
      this.mediaElement.srcObject = this.props.stream;
      this.mediaElement.muted = true;
    }
  }

  private onSave = () => {
    const {
      audioOutputDeviceId,
      audioInputDeviceId,
      videoInputDeviceId,
      videoQualityId,

      saveVideoSettings,
      onSave,
    } = this.props;

    const props: IVideoSettings = {
      audioOutputDeviceId,
      audioInputDeviceId,
      videoInputDeviceId,
      videoQualityId,
    };

    const changes: ISettingsChanges = getSettingsChanges(props);

    saveVideoSettings(props);
    onSave(changes);
    location.reload();
  }
}
